import { APP_INITIALIZER, LOCALE_ID, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ConfigService } from './config.service';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { AddHostInterceptor } from './add-host.interceptor';
import { LoginComponent } from './auth/login/login.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthInterceptor } from './auth/auth.interceptor';
import { LeftMenuComponent } from './left-menu/left-menu.component';
import { ComponentsComponent } from './components/components.component';
import { InventoryComponent } from './inventory/inventory.component';
import { BgfoxTermekekComponent } from './bgfox-termekek/bgfox-termekek.component';
import { OrdersComponent } from './orders/orders.component';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {MatTableModule} from '@angular/material/table';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatInputModule} from '@angular/material/input';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {provideNativeDateAdapter} from '@angular/material/core';
import {
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatPaginatorModule } from '@angular/material/paginator';
import { ComponentEditorComponent } from './components/component-editor/component-editor.component';
import { EtsyComponent } from './merchants/etsy/etsy.component';
import { AfterValueChangedDirective } from './after-value-changed.directive';
import { NotificationComponent } from './notification/notification.component';
import { ProductEditorComponent } from './inventory/product-editor/product-editor.component';
import { MatSortModule } from '@angular/material/sort';
import { BgfAutoSelectorComponent } from './utils/bgf-auto-selector/bgf-auto-selector.component';
import { AddStockComponent } from './add-stock/add-stock.component';
import { MatSelectModule } from '@angular/material/select';
import { TransactionsComponent } from './transactions/transactions.component';
import { StockHighlightPipe } from './inventory/stock-highlight.pipe';
import localeHu from '@angular/common/locales/hu';
import { registerLocaleData } from '@angular/common';
import { ShopifyComponent } from './merchants/shopify/shopify.component';
import { ComponentImageComponent } from './component-image/component-image.component';
import { FetchComponentImageDirective } from './fetch-component-image.directive';
import { FetchUserImageDirective } from './fetch-user-image.directive';
import { UserAvatarDirective } from './users/user-avatar.directive';
import { FetchSupportImageDirective } from './fetch-support-image.directive';
import { StockReallocComponent } from './stokker/stock-realloc/stock-realloc.component';
import { ComponentBomComponent } from './component-bom/component-bom.component';
import { ComponentBomItemComponent } from './component-bom/component-bom-item/component-bom-item.component';
import { HeaderComponent } from './header/header.component';
import { OrderDetailsComponent } from './order-details/order-details.component';
import { GamesComponent } from './games/games.component';
import { EditGameComponent } from './games/edit-game/edit-game.component';
import { UsersComponent } from './users/users.component';
import { EditUserComponent } from './users/edit-user/edit-user.component';
import { CarriersComponent } from './carriers/carriers.component';
import { EditCarrierComponent } from './carriers/edit-carrier/edit-carrier.component';
import { EditPreferredComponent } from './carriers/edit-preferred/edit-preferred.component';
import { StockSnapshotsComponent } from './stock-snapshots/stock-snapshots.component';
import { DashboardsComponent } from './dashboards/dashboards.component';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { LabelDialogComponent } from './label-dialog/label-dialog.component';
import { FoxpostDialogComponent } from './foxpost-dialog/foxpost-dialog.component';
import { SettingsComponent } from './settings/settings.component';
import { MaiNapComponent } from './stokker/mai-nap/mai-nap.component';
import { RaktarSzabalyComponent } from './stokker/raktar-szabaly/raktar-szabaly.component';
import { GyartasComponent } from './stokker/gyartas/gyartas.component';
import { BeszerzesComponent } from './stokker/beszerzes/beszerzes.component';
import { RendelesiListaComponent } from './stokker/beszerzes/rendelesi-lista/rendelesi-lista.component';
import { BeszerzesRendelesekComponent } from './stokker/beszerzes/rendelesek/rendelesek.component';
import { BeszallitokComponent } from './stokker/beszerzes/beszallitok/beszallitok.component';
import { TrackingPanelComponent } from './stokker/tracking-panel/tracking-panel.component';
import { OrderSupportPanelComponent } from './stokker/order-support-panel/order-support-panel.component';
import { OrderChangeLogComponent } from './stokker/order-change-log/order-change-log.component';
import { AnyagokComponent } from './stokker/anyagok/anyagok.component';
import { SzamlazasComponent } from './stokker/szamlazas/szamlazas.component';
import { FutarRendelesComponent } from './stokker/futar-rendeles/futar-rendeles.component';
import { SupportComponent } from './stokker/support/support.component';
import { SupportKezikonyvComponent } from './stokker/support/kezikonyv/support-kezikonyv.component';
import { SupportMailComponent } from './stokker/support/levelezes/support-mail.component';
import { MobileTabBarComponent } from './mobile-tab-bar/mobile-tab-bar.component';
import { PwaBannerComponent } from './pwa/pwa-banner.component';
import { ErtesitesekComponent } from './ertesitesek/ertesitesek.component';
import { ChatComponent } from './chat/chat.component';
import { FoxiComponent } from './foxi/foxi.component';
import { BorisComponent } from './boris/boris.component';
import { FarmyComponent } from './farmy/farmy.component';
import { ChatThreadComponent } from './chat/chat-thread.component';
import { ChatRefPickerComponent } from './chat/chat-ref-picker.component';
import { ChatBlobPipe } from './chat/chat-blob.pipe';
import { ChatModelViewerComponent } from './chat/chat-model-viewer.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { MunkaidoComponent } from './stokker/munkaido/munkaido.component';
import { MunkaidoAdminComponent } from './stokker/munkaido/admin/munkaido-admin.component';
import { WtPlansComponent } from './stokker/munkaido/plans/wt-plans.component';
import { RiportokComponent } from './stokker/riportok/riportok.component';
import { TrackingComponent } from './stokker/tracking/tracking.component';

registerLocaleData(localeHu);

function initialize(config: ConfigService) {
  return (): Promise<boolean> => {
    return config.getConfig();
  }
};

@NgModule({
  declarations: [
    AppComponent,
    ChatComponent,
    FoxiComponent,
    BorisComponent,
    FarmyComponent,
    ChatThreadComponent,
    ChatRefPickerComponent,
    ChatBlobPipe,
    ChatModelViewerComponent,
    LoginComponent,
    LeftMenuComponent,
    ComponentsComponent,
    InventoryComponent,
    BgfoxTermekekComponent,
    OrdersComponent,
    ComponentEditorComponent,
    EtsyComponent,
    AfterValueChangedDirective,
    NotificationComponent,
    ProductEditorComponent,
    BgfAutoSelectorComponent,
    AddStockComponent,
    TransactionsComponent,
    StockHighlightPipe,
    ShopifyComponent,
    ComponentImageComponent,
    FetchComponentImageDirective,
    FetchUserImageDirective,
    UserAvatarDirective,
    FetchSupportImageDirective,
    StockReallocComponent,
    ComponentBomComponent,
    ComponentBomItemComponent,
    HeaderComponent,
    OrderDetailsComponent,
    GamesComponent,
    EditGameComponent,
    UsersComponent,
    EditUserComponent,
    CarriersComponent,
    EditCarrierComponent,
    EditPreferredComponent,
    StockSnapshotsComponent,
    DashboardsComponent,
    ConfirmDialogComponent,
    LabelDialogComponent,
    FoxpostDialogComponent,
    SettingsComponent,
    MaiNapComponent,
    RaktarSzabalyComponent,
    GyartasComponent,
    BeszerzesComponent,
    RendelesiListaComponent,
    BeszerzesRendelesekComponent,
    BeszallitokComponent,
    TrackingPanelComponent,
    OrderSupportPanelComponent,
    OrderChangeLogComponent,
    AnyagokComponent,
    SzamlazasComponent,
    FutarRendelesComponent,
    SupportComponent,
    SupportKezikonyvComponent,
    SupportMailComponent,
    MobileTabBarComponent,
    PwaBannerComponent,
    ErtesitesekComponent,
    MunkaidoComponent,
    MunkaidoAdminComponent,
    WtPlansComponent,
    RiportokComponent,
    TrackingComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    // PWA: csak production buildben él (fejlesztés közben a gyorsítótár csak zavarna)
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    MatTableModule,
    MatPaginatorModule,
    MatSlideToggleModule,
    MatAutocompleteModule,
    MatSortModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatSelectModule,
    MatDatepickerModule,
    MatInputModule
  ],
  providers: [
    {provide: LOCALE_ID, useValue: 'hu-HU' },
    {
      provide: APP_INITIALIZER,
      useFactory: initialize,
      deps: [
        ConfigService
      ],
      multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: AddHostInterceptor, multi: true},
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
    provideAnimationsAsync(),
    provideNativeDateAdapter()
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
